import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { GraduationCap } from "@phosphor-icons/react";

const SBL_LOGO = "/sbl-logo.jpeg";

export default function RegisterPage() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const presetMode = searchParams.get("mode");
  const presetProgrammeId = searchParams.get("programme");

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [phone, setPhone] = useState("");
  const [employer, setEmployer] = useState("");
  const [position, setPosition] = useState("");
  // When ?mode=programme, lock interest_type to "programme" and skip the radio.
  const [interestType, setInterestType] = useState(presetMode === "programme" ? "programme" : "");
  const [presetProgrammeTitle, setPresetProgrammeTitle] = useState("");
  const [allProgrammes, setAllProgrammes] = useState([]);
  const [selectedProgrammes, setSelectedProgrammes] = useState(() => presetProgrammeId ? [presetProgrammeId] : []);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (presetProgrammeId) {
      api.get(`/programmes/${presetProgrammeId}`).then(r => setPresetProgrammeTitle(r.data?.title || "")).catch(() => {});
    }
    // For Task 3 — show the public catalogue so a self-signup visitor can pick
    // additional programmes during account creation.
    if (presetMode === "programme") {
      api.get("/public-programmes").then(r => setAllProgrammes(r.data || [])).catch(() => {});
    }
  }, [presetProgrammeId, presetMode]);

  const toggleProgramme = (id) => {
    setSelectedProgrammes(curr => curr.includes(id) ? curr.filter(x => x !== id) : [...curr, id]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 6) { setError("Password must be at least 6 characters"); return; }
    if (!employer.trim()) { setError("Current employer / school name is required"); return; }
    if (!position.trim()) { setError("Your role / position is required"); return; }
    if (!interestType) { setError("Please indicate what you're interested in"); return; }
    setLoading(true);
    const result = await register(name, email, password, {
      phone, school_name: employer, position,
      interest_type: interestType,
      programme_ids: selectedProgrammes,
    });
    setLoading(false);
    if (!result.success) { setError(result.error); return; }
    // After self-signup with programmes selected, land them on /my-programmes
    // where they can Pay Now / Withdraw on each picked programme.
    if (selectedProgrammes.length > 0) navigate("/my-programmes");
    else if (presetMode === "programme" && presetProgrammeId) navigate(`/apply/${presetProgrammeId}`);
    else navigate("/dashboard");
  };

  return (
    <div className="min-h-screen bg-slate-50 flex">
      <div className="hidden lg:flex lg:w-1/2 bg-slate-950 text-white items-center justify-center p-12">
        <div className="max-w-md">
          <Link to="/" className="flex items-center gap-3 mb-8"><img src={SBL_LOGO} alt="SBL" className="w-12 h-12 object-contain rounded-sm" /><span className="text-2xl font-bold tracking-tight" style={{ fontFamily: "'Outfit', sans-serif" }}>School Builder Lab Africa</span></Link>
          <h1 className="text-3xl font-bold tracking-tight mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>Build. Lead. Transform.</h1>
          <p className="text-slate-400 leading-relaxed">Join a growing community of school leaders across Africa. Access world-class programmes, AI-powered learning tools, and expert instructors.</p>
        </div>
      </div>
      <div className="flex-1 flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-md">
          <Link to="/" className="flex items-center gap-2 mb-6 lg:hidden">
            <img src={SBL_LOGO} alt="SBL" className="w-10 h-10 object-contain rounded-sm" />
            <span className="text-xl font-bold tracking-tight text-slate-950" style={{ fontFamily: "'Outfit', sans-serif" }}>School Builder Lab Africa</span>
          </Link>
          <h2 className="text-2xl font-semibold tracking-tight text-slate-900 mb-1" style={{ fontFamily: "'Outfit', sans-serif" }}>Create Account</h2>
          <p className="text-sm text-slate-500 mb-6">Start your leadership journey today</p>

          {presetMode === "programme" && presetProgrammeId && (
            <div className="bg-amber-50 border border-amber-200 px-4 py-3 mb-4" data-testid="apply-mode-banner">
              <p className="text-xs uppercase tracking-widest font-bold text-amber-700">Applying for</p>
              <p className="text-sm font-bold text-slate-950">{presetProgrammeTitle || "Programme"}</p>
              <p className="text-[11px] text-slate-600 mt-1">After signup we'll take you straight to the payment page.</p>
            </div>
          )}

          {error && <div className="bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3 mb-4" data-testid="register-error">{error}</div>}

          <form onSubmit={handleSubmit} className="space-y-3">
            <div>
              <Label className="text-slate-700 text-sm font-medium">Full Name <span className="text-red-500">*</span></Label>
              <Input placeholder="Enter your full name" value={name} onChange={e => setName(e.target.value)} required data-testid="register-name-input" className="mt-1 rounded-none border-slate-300" />
            </div>
            <div>
              <Label className="text-slate-700 text-sm font-medium">Email <span className="text-red-500">*</span></Label>
              <Input type="email" placeholder="you@example.com" value={email} onChange={e => setEmail(e.target.value)} required data-testid="register-email-input" className="mt-1 rounded-none border-slate-300" />
            </div>
            <div>
              <Label className="text-slate-700 text-sm font-medium">Password <span className="text-red-500">*</span></Label>
              <Input type="password" placeholder="Min 6 characters" value={password} onChange={e => setPassword(e.target.value)} required data-testid="register-password-input" className="mt-1 rounded-none border-slate-300" />
            </div>
            <div>
              <Label className="text-slate-700 text-sm font-medium">Phone Number</Label>
              <Input type="tel" placeholder="+234..." value={phone} onChange={e => setPhone(e.target.value)} data-testid="register-phone-input" className="mt-1 rounded-none border-slate-300" />
            </div>
            <div>
              <Label className="text-slate-700 text-sm font-medium">Current Employer / School Name <span className="text-red-500">*</span></Label>
              <Input placeholder="e.g. Greenfield Academy" value={employer} onChange={e => setEmployer(e.target.value)} required data-testid="register-employer-input" className="mt-1 rounded-none border-slate-300" />
            </div>
            <div>
              <Label className="text-slate-700 text-sm font-medium">Your Role / Position <span className="text-red-500">*</span></Label>
              <Input placeholder="e.g. Vice Principal, Operations Manager" value={position} onChange={e => setPosition(e.target.value)} required data-testid="register-position-input" className="mt-1 rounded-none border-slate-300" />
            </div>
            {presetMode !== "programme" && (
            <div>
              <Label className="text-slate-700 text-sm font-medium">I'm signing up to <span className="text-red-500">*</span></Label>
              <Select value={interestType} onValueChange={setInterestType}>
                <SelectTrigger className="mt-1 rounded-none border-slate-300" data-testid="register-interest-type">
                  <SelectValue placeholder="Select what you're interested in" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="free_course">Take Free Courses</SelectItem>
                  <SelectItem value="paid_course">Take a Paid Course</SelectItem>
                  <SelectItem value="programme">Enroll in a Programme</SelectItem>
                </SelectContent>
              </Select>
            </div>
            )}
            {presetMode === "programme" && allProgrammes.length > 0 && (
              <div data-testid="register-programmes-picker">
                <Label className="text-slate-700 text-sm font-medium">Programmes you want to enrol in <span className="text-red-500">*</span></Label>
                <p className="text-[11px] text-slate-500 mt-0.5 mb-2">Selected programmes will appear in your dashboard right after sign-up — pay or withdraw at your pace.</p>
                <div className="border border-slate-200 max-h-48 overflow-y-auto">
                  {allProgrammes.map(p => {
                    const checked = selectedProgrammes.includes(p.id);
                    return (
                      <label key={p.id} className="flex items-center gap-2 text-xs cursor-pointer hover:bg-slate-50 px-2 py-1.5 border-b border-slate-100 last:border-b-0">
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleProgramme(p.id)}
                          data-testid={`register-prog-${p.id}`}
                        />
                        <span className="font-mono text-amber-700">{p.code || "—"}</span>
                        <span className="truncate text-slate-700">{p.title}</span>
                      </label>
                    );
                  })}
                </div>
                {selectedProgrammes.length > 0 && (
                  <p className="text-[11px] text-emerald-700 mt-1 font-semibold">{selectedProgrammes.length} programme{selectedProgrammes.length === 1 ? "" : "s"} selected</p>
                )}
              </div>
            )}
            <Button type="submit" disabled={loading} data-testid="register-submit-btn" className="w-full bg-blue-800 text-white hover:bg-blue-900 rounded-none font-medium h-11 mt-2">
              {loading ? "Creating account..." : "Create Account"}
            </Button>
          </form>
          <p className="text-center text-sm text-slate-500 mt-6">Already have an account? <Link to="/login" className="text-blue-800 font-medium hover:underline" data-testid="go-to-login">Sign In</Link></p>
        </div>
      </div>
    </div>
  );
}
